import Routing, { type RouteName } from "routing";

import Uri from "@app:pages/common/vendor/jsuri-1.3.1";

import type {
  CreatedLocation,
  LocationAddressDetail,
  LocationAddressSuggestion,
  LocationCity,
  LocationCountry,
  LocationTypedAddress,
} from "../types";

type RequestOptions = { signal?: AbortSignal };

export interface CitySearchFilter {
  q?: string;
  countryIso2?: string;
}

export interface AddressSearchFilter {
  q?: string;
  countryIso2?: string;
}

export interface CreateAddressInput {
  contactId: number;
  place?: string;
  street?: string;
  extendedAddress?: string;
  postOfficeBox?: string;
  postalCode?: string;
  city?: string;
  country?: string;
}

export interface UpdateAddressInput {
  place?: string;
  street?: string;
  extendedAddress?: string;
  postOfficeBox?: string;
  postalCode?: string;
  city?: string;
  country?: string;
}

export interface CreateLocationInput {
  contactId: number;
  name?: string;
  addressId?: number;
}

export interface LocationApi {
  fetchContactAddresses(
    contactId: number,
    options?: RequestOptions,
  ): Promise<LocationTypedAddress[]>;
  fetchCountries(options?: RequestOptions): Promise<LocationCountry[]>;
  searchCities(
    filter: CitySearchFilter,
    options?: RequestOptions,
  ): Promise<LocationCity[]>;
  searchAddresses(
    filter: AddressSearchFilter,
    options?: RequestOptions,
  ): Promise<LocationAddressSuggestion[]>;
  createAddress(
    input: CreateAddressInput,
    options?: RequestOptions,
  ): Promise<LocationTypedAddress>;
  fetchAddress(
    addressId: number,
    options?: RequestOptions,
  ): Promise<LocationAddressDetail>;
  updateAddress(
    addressId: number,
    input: UpdateAddressInput,
    options?: RequestOptions,
  ): Promise<LocationAddressDetail>;
  createLocation(
    input: CreateLocationInput,
    options?: RequestOptions,
  ): Promise<CreatedLocation>;
}

const generateUri = (
  routeName: RouteName,
  params: Record<string, any> = {},
): Uri => {
  const uri = new Uri(Routing.generate(routeName, params));

  const routeVariableNames = Routing.getVariableNames(routeName);

  for (const [key, value] of Object.entries(params)) {
    // Skip parameters that are part of the path
    if (routeVariableNames.includes(key)) {
      continue;
    }

    uri.replaceQueryParam(key, value);
  }

  return uri;
};

export const locationApi: LocationApi = {
  async fetchContactAddresses(contactId, options) {
    const uri = generateUri("api_typedaddresses_list", { contactId });

    const response = await fetch(uri.toString(), options);

    if (!response.ok) {
      throw new Error(`Failed to fetch addresses: ${response.statusText}`);
    }

    return response.json();
  },

  async fetchCountries(options) {
    const uri = generateUri("api_referential_countries_list");

    const response = await fetch(uri.toString(), options);

    if (!response.ok) {
      throw new Error(`Failed to fetch countries: ${response.statusText}`);
    }

    return response.json();
  },

  async searchCities(filter, options) {
    const uri = generateUri("api_search_referential_cities_filter");

    const response = await fetch(uri.toString(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ filter }),
      ...options,
    });

    if (!response.ok) {
      throw new Error(`Failed to search cities: ${response.statusText}`);
    }

    const data = await response.json();

    return data.results;
  },

  async searchAddresses(filter, options) {
    const uri = generateUri("api_search_referential_addresses_filter");

    const response = await fetch(uri.toString(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ filter }),
      ...options,
    });

    if (!response.ok) {
      throw new Error(`Failed to search addresses: ${response.statusText}`);
    }

    const data = await response.json();

    return data.results;
  },

  async createAddress(input, options) {
    const uri = generateUri("api_typedaddresses_create");

    const response = await fetch(uri.toString(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ addressInput: input }),
      ...options,
    });

    if (!response.ok) {
      throw new Error(`Failed to create address: ${response.statusText}`);
    }

    return response.json();
  },

  async fetchAddress(addressId, options) {
    const uri = generateUri("api_typedaddresses_show", { addressId });

    const response = await fetch(uri.toString(), options);

    if (!response.ok) {
      throw new Error(`Failed to fetch address: ${response.statusText}`);
    }

    return response.json();
  },

  async updateAddress(addressId, input, options) {
    const uri = generateUri("api_typedaddresses_update", { addressId });

    const response = await fetch(uri.toString(), {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ addressInput: input }),
      ...options,
    });

    if (!response.ok) {
      throw new Error(`Failed to update address: ${response.statusText}`);
    }

    return response.json();
  },

  async createLocation(input, options) {
    const uri = generateUri("api_locations_create");

    const response = await fetch(uri.toString(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ locationInput: input }),
      ...options,
    });

    if (!response.ok) {
      throw new Error(`Failed to create location: ${response.statusText}`);
    }

    return response.json();
  },
};
